import {
  decrementTotalFeedbacks,
  getSummarizingFeedbackHistory,
  removeSummarizingFeedback,
  setPage,
  setTotalPages,
} from "@/redux/slices/mlSlices/summarizationSlice";

import {useCallback, useEffect, useMemo} from "react";

import {Pagination} from "@layouts/page/TableLayout/Pagination";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {DeleteDataPopup} from "@popups/attentionPopups/DeleteDataPopup";
import {BackButton} from "@ui/buttons/BackButton";
import {TableButton} from "@ui/buttons/TableButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {Spinner} from "@ui/general/Spinner";
import {WarningData} from "@ui/general/WarningData";
import {H3} from "@ui/titles/H3";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import styles from "./styles.module.scss";

export const FeedbackHistory = ({onClickView, onClickBack}) => {
  const dispatch = useDispatch();
  const {openPopup, closePopup} = usePopup();

  const userRole = useSelector((state) => state.common.candidate.role);
  const userId = useSelector((state) => state.common.candidate.id);
  const targetUserId = useSelector((state) => state?.user?.view?.current?.id);
  const {
    feedbackData,
    totalPages,
    totalFeedbacks,
    size,
    page,
    error,
    serverStatus,
  } = useSelector((state) => state.ml.summarization);
  const {demo} = useSelector((state) => state.common.candidate);

  const {t} = useTranslation();

  const toPage = (pageIndex) => {
    dispatch(setPage(pageIndex + 1));
  };

  const removeFeedback = useCallback(async (id) => {
    try {
      const resultAction = await dispatch(removeSummarizingFeedback({id}));

      if (removeSummarizingFeedback.fulfilled.match(resultAction)) {
        dispatch(decrementTotalFeedbacks());
      }
    } catch (e) {
      console.log("Error deleting product: ", e);
    }
  }, []);

  const handleClickDeleteFeedback = async (item) => {
    openPopup(
      <DeleteDataPopup
        onClickYes={() => removeFeedback(item.id)}
        onClickNo={() => closePopup()}
      />,
      {closeButton: false},
    );
  };

  const requestUserId = useMemo(() => {
    if (userRole !== "Admin" && userId === targetUserId) {
      return userId;
    }
    return targetUserId || userId;
  }, [userRole, userId, targetUserId]);

  useEffect(() => {
    const params = {
      page: page > 0 ? page : 1,
      size: size > 0 ? size : 5,
    };

    if (requestUserId) {
      dispatch(getSummarizingFeedbackHistory({id: requestUserId, ...params}));
    }
  }, [page, size, requestUserId, dispatch]);

  useEffect(() => {
    dispatch(setTotalPages(Math.ceil(totalFeedbacks / size)));
  }, [totalFeedbacks, size, dispatch]);

  useEffect(() => {
    if (page === totalPages + 1 && totalPages > 0) {
      dispatch(setPage(Math.ceil(totalFeedbacks / size)));
    }
  }, [totalPages, totalFeedbacks, size, page, dispatch]);

  return (
    <div className={styles.feedbackHistory}>
      {serverStatus === "loading" ? (
        <Spinner />
      ) : serverStatus === "error" ? (
        <WarningData type={"error"} />
      ) : feedbackData?.length === 0 ? (
        <WarningData type={"empty"} />
      ) : (
        <>
          <BackButton onClick={() => onClickBack()} />

          <ul className={styles.feedbackHistoryList}>
            {feedbackData &&
              feedbackData.length > 0 &&
              feedbackData.map((item) => (
                <li key={item?.id}>
                  <div style={{justifyContent: "flex-start"}}>
                    <H3>
                      <b>
                        {item?.title === ""
                          ? t("table.sum_title_default")
                          : item?.title}
                      </b>
                    </H3>
                  </div>

                  <div>
                    <RootDesc>
                      <span>
                        {moment(item?.start_date).format("DD MMMM YYYY")} -{" "}
                        {moment(item?.end_date).format("DD MMMM YYYY")}
                      </span>
                    </RootDesc>
                  </div>

                  <div>
                    <RootDesc>
                      <span style={{color: "var(--grey)"}}>
                        {t("page.profile.feedback.generated")}
                        {moment(item?.created_at).format("DD MMMM YYYY")}
                      </span>
                    </RootDesc>
                  </div>

                  <div className={styles.feedbackHistoryBtn}>
                    <TableButton
                      icon={"view"}
                      color={"var(--orange)"}
                      label={t("table.button.view")}
                      action={"item"}
                      item={item}
                      onClickAction={() => onClickView(item)}
                    />

                    {!demo && (
                      <TableButton
                        icon={"delete"}
                        color={"var(--red)"}
                        label={t("table.button.delete")}
                        item={item}
                        title={t("page.profile.feedback.delete_btn_title")}
                        action={"delete"}
                        onClickAction={() => handleClickDeleteFeedback(item)}
                        disabled={
                          moment().diff(moment(item?.created_at), "hours") <
                            24 ||
                          (item?.status !== "completed" &&
                            item?.status === "failed")
                        }
                      />
                    )}
                  </div>
                </li>
              ))}
          </ul>

          {error === "" && (
            <Pagination
              data={feedbackData}
              totalPages={totalPages}
              page={page}
              toPage={toPage}
            />
          )}
        </>
      )}
    </div>
  );
};
